import type {
  ChecklistItemResponse,
  ChecklistItemStatus,
  ChecklistPriority,
  ChecklistRequestKind,
  ChecklistSectionResponse,
  ChecklistStatusRollup,
} from '../api/types'
import {
  CHECKLIST_STATUSES,
  UNCATEGORIZED_SECTION,
  checklistRollup,
  matchesChecklistSearch,
} from './checklist'

/*
  The request list as a deal lead reads it each morning (ADR 0009): the workbook's cover sheet,
  and the list narrowed to what is mine, late, or waiting for me. Pure functions, so the numbers
  and the URL round trip are tested without a browser.

  THE MATRIX IS COMPUTED HERE, FROM THE LIST THE SCREEN ALREADY HOLDS (ADR 0009 s7). There is no
  rollup route: the API returns every item to anyone who may read the list, so a matrix built
  from those rows reconciles with them by construction, and there is no second place for the
  four-bucket rule to drift.
*/

export const ROLLUP_BUCKETS: ReadonlyArray<ChecklistStatusRollup> = [
  'Not started',
  'WIP',
  'Done',
  'N/A',
]

export interface MatrixCell {
  count: number
  /** Share of the row's total, 0 to 100, rounded; 0 when the row is empty. */
  percent: number
}

export interface MatrixRow {
  /** Null for the items that have no section. */
  sectionId: string | null
  name: string
  total: number
  /** The workbook's four buckets, in ROLLUP_BUCKETS order. */
  buckets: MatrixCell[]
  /** The six states, in CHECKLIST_STATUSES order, behind the toggle. */
  states: MatrixCell[]
  /** Answered = Done + N/A: what the workbook's "complete" means. */
  answered: number
  overdue: number
}

export interface ChecklistMatrix {
  rows: MatrixRow[]
  total: MatrixRow
  /**
   * High priority reported on its own: a list that is 90% complete with three open High
   * requests is not 90% done, and the Master DRL has 41 of them.
   */
  high: { total: number; answered: number; overdue: number }
}

const percent = (count: number, total: number) =>
  total === 0 ? 0 : Math.round((count / total) * 100)

type MatrixItem = Pick<ChecklistItemResponse, 'status' | 'sectionId' | 'priority' | 'isOverdue'>

function matrixRow(sectionId: string | null, name: string, items: MatrixItem[]): MatrixRow {
  const total = items.length
  const cell = (count: number): MatrixCell => ({ count, percent: percent(count, total) })
  const buckets = ROLLUP_BUCKETS.map((b) =>
    cell(items.filter((i) => checklistRollup(i.status) === b).length),
  )
  return {
    sectionId,
    name,
    total,
    buckets,
    states: CHECKLIST_STATUSES.map((s) => cell(items.filter((i) => i.status === s).length)),
    answered: (buckets[2]?.count ?? 0) + (buckets[3]?.count ?? 0),
    overdue: items.filter((i) => i.isOverdue === true).length,
  }
}

/** Status × workstream, with a total row: the cover sheet of the deal team's workbook. */
export function checklistMatrix(
  sections: ReadonlyArray<ChecklistSectionResponse>,
  items: ReadonlyArray<MatrixItem>,
): ChecklistMatrix {
  const known = new Set(sections.map((s) => s.id))
  const rows = sections
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((s) =>
      matrixRow(
        s.id,
        s.name,
        items.filter((i) => i.sectionId === s.id),
      ),
    )
  const loose = items.filter((i) => i.sectionId === null || !known.has(i.sectionId))
  if (loose.length > 0) rows.push(matrixRow(null, UNCATEGORIZED_SECTION, loose))

  const high = items.filter((i) => i.priority === 'High')
  return {
    rows,
    total: matrixRow(null, 'Total', items.slice()),
    high: {
      total: high.length,
      answered: high.filter(
        (i) => checklistRollup(i.status) === 'Done' || checklistRollup(i.status) === 'N/A',
      ).length,
      overdue: high.filter((i) => i.isOverdue === true).length,
    },
  }
}

// ------------------------------------------------------------------ filters, kept in the URL

/**
 * What the list is narrowed to. It lives in the URL query string and nowhere else: the CSP
 * forbids nothing here, but the product keeps no localStorage or sessionStorage, and a filtered
 * view in the URL is one a deal lead can paste into an email.
 */
export interface ChecklistFilters {
  /** A section id, or 'none' for the items that have no section. */
  section: string | null
  category: string | null
  status: ChecklistItemStatus | null
  priority: ChecklistPriority | null
  kind: ChecklistRequestKind | null
  mine: boolean
  /** Still the responder's to answer: Open, InProgress or NeedsRevision. */
  open: boolean
  overdue: boolean
  /** true: has documents; false: has none; null: either. */
  documents: boolean | null
  text: string
}

export const NO_FILTERS: ChecklistFilters = {
  section: null,
  category: null,
  status: null,
  priority: null,
  kind: null,
  mine: false,
  open: false,
  overdue: false,
  documents: null,
  text: '',
}

const PRIORITIES: ReadonlyArray<ChecklistPriority> = ['High', 'Medium', 'Low']
const KINDS: ReadonlyArray<ChecklistRequestKind> = ['Static', 'Dynamic']

const oneOf = <T extends string>(value: string | null, allowed: ReadonlyArray<T>): T | null =>
  value !== null && (allowed as ReadonlyArray<string>).includes(value) ? (value as T) : null

/** A URL is something a person can type: anything unrecognised is ignored, never an error. */
export function filtersFromSearch(params: URLSearchParams): ChecklistFilters {
  const docs = params.get('docs')
  return {
    section: params.get('ws'),
    category: params.get('cat'),
    status: oneOf(params.get('status'), CHECKLIST_STATUSES),
    priority: oneOf(params.get('priority'), PRIORITIES),
    kind: oneOf(params.get('kind'), KINDS),
    mine: params.get('mine') === '1',
    open: params.get('open') === '1',
    overdue: params.get('overdue') === '1',
    documents: docs === '1' ? true : docs === '0' ? false : null,
    text: params.get('q') ?? '',
  }
}

/** Only what is set is written, so an unfiltered list has a clean URL. */
export function filtersToSearch(filters: ChecklistFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.section) params.set('ws', filters.section)
  if (filters.category) params.set('cat', filters.category)
  if (filters.status) params.set('status', filters.status)
  if (filters.priority) params.set('priority', filters.priority)
  if (filters.kind) params.set('kind', filters.kind)
  if (filters.mine) params.set('mine', '1')
  if (filters.open) params.set('open', '1')
  if (filters.overdue) params.set('overdue', '1')
  if (filters.documents !== null) params.set('docs', filters.documents ? '1' : '0')
  if (filters.text.trim()) params.set('q', filters.text.trim())
  return params
}

export const hasFilters = (filters: ChecklistFilters): boolean =>
  filtersToSearch(filters).toString() !== ''

/** The responder still owes it: the states overdue is judged on (ADR 0009 s8). */
export const isAwaitingResponder = (status: string): boolean =>
  status === 'Open' || status === 'InProgress' || status === 'NeedsRevision'

export function applyChecklistFilters<T extends ChecklistItemResponse>(
  items: ReadonlyArray<T>,
  filters: ChecklistFilters,
): T[] {
  return items.filter((i) => {
    if (
      filters.section === 'none'
        ? i.sectionId !== null
        : filters.section && i.sectionId !== filters.section
    )
      return false
    if (filters.category && (i.category ?? '') !== filters.category) return false
    if (filters.status && i.status !== filters.status) return false
    if (filters.priority && i.priority !== filters.priority) return false
    if (filters.kind && i.requestKind !== filters.kind) return false
    if (filters.mine && i.assignedToMe !== true) return false
    if (filters.open && !isAwaitingResponder(i.status)) return false
    if (filters.overdue && i.isOverdue !== true) return false
    if (filters.documents !== null && i.documentCount > 0 !== filters.documents) return false
    return matchesChecklistSearch(i, filters.text)
  })
}

/** The two buttons, which are filters and nothing stored: "My open items" and "Awaiting my review". */
export const MY_OPEN_ITEMS: ChecklistFilters = { ...NO_FILTERS, mine: true, open: true }
export const AWAITING_MY_REVIEW: ChecklistFilters = { ...NO_FILTERS, status: 'Submitted' }

/** "30 Sep 2026" from "2026-09-30", without a time zone having a say: a goal date is a date. */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return ''
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return value
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${d} ${months[m - 1] ?? ''} ${y}`
}
